import React, { useState, useContext, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { Table, Button, Tag, Switch, Popconfirm, notification as userNotification } from 'antd';
import { DeleteOutlined, EditOutlined, HomeOutlined, PlusOutlined } from '@ant-design/icons';
import { PageContainer } from '@ant-design/pro-layout';
import userService from 'services/user.service';
import AddForm from 'views/User/components/AddForm';
import { removeProperty } from 'helpers/utils';
import 'antd/dist/reset.css';
import { AuthContext } from '../../context';
import CheckAuth from '../../components/common/CheckAuth';
import authorities from '../../helpers/authorities';

const User = () => {
  const context = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  const getUsers = async () => {
    const number = context?.selectedMerch?.number;
    try {
      setLoading(true);
      const usersData = await userService.getSubUsers(number);
      setUsers(usersData?.payload);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      userNotification.error({
        message: 'İşlem Başarısız',
        description: error,
      });
    }
  };

  useEffect(() => {
    if (context?.selectedMerch?.number) {
      getUsers();
    }
    // eslint-disable-next-line
  }, [context?.selectedMerch?.number]);

  const handleCloseForm = () => {
    setVisible(false);
    setSelectedUser(null);
    setErrors(null);
  };

  const handleSubmitForm = async (id, body) => {
    const number = context?.selectedMerch?.number;
    try {
      setSubmitting(true);
      const removeKeys = removeProperty('keys');
      const removeSelectedMerchs = removeProperty('selectedMerchs');
      if (selectedUser) {
        await userService.updateUser(
          selectedUser.identifier,
          removeSelectedMerchs(removeKeys(body)),
        );
        userNotification.success({
          message: 'İşlem Başarılı',
          description: 'Kullanıcı güncellendi.',
        });
      } else {
        await userService.createUser(id, number, removeSelectedMerchs(removeKeys(body)));
        userNotification.success({
          message: 'İşlem Başarılı',
          description: 'Kullanıcı oluşturuldu.',
        });
      }
      setSubmitting(false);
      setErrors(null);
      handleCloseForm();
      await getUsers();
    } catch (error) {
      setSubmitting(false);
      userNotification.error({
        message: 'İşlem Başarısız',
        description: error.message || 'Bir hata oluştu. Lütfen tekrar deneyiniz.',
      });
    }
  };

  const handleStatusChange = async (status, record) => {
    try {
      await userService.setUserStatus(record?.identifier, status);
      userNotification.success({
        message: 'İşlem Başarılı',
        description: status ? 'Kullanıcı aktifleştirildi.' : 'Kullanıcı pasifleştirildi.',
      });
    } catch (error) {
      userNotification.error({
        message: 'İşlem Başarısız',
        description: error.message || 'Bir hata oluştu. Lütfen tekrar deneyiniz.',
      });
      await getUsers();
    }
  };

  const deleteUser = async (id) => {
    try {
      setLoading(true);
      await userService.deleteUser(id);
      userNotification.success({
        message: 'İşlem Başarılı',
        description: 'Kullanıcı silindi.',
      });
      await getUsers();
    } catch (error) {
      setLoading(false);
      userNotification.error({
        message: 'İşlem Başarısız',
        description: error.message || 'Bir hata oluştu. Lütfen tekrar deneyiniz.',
      });
    }
  };

  const handleOpenForm = () => {
    setVisible(true);
  };

  const columns = [
    {
      title: <div className="table-title-calibri-bold-12">E-Posta</div>,
      dataIndex: 'email',
      key: 'email',
      align: 'center',
      render: (_, row) => <span className="table-title-calibri-bold-12">{row?.email}</span>,
    },
    {
      title: <div className="table-title-calibri-bold-12">Onay Durumu</div>,
      dataIndex: 'confirmed',
      key: 'confirmed',
      align: 'center',
      width: 200,
      render: (confirmed, record) => (
        <Tag color={confirmed ? 'green' : 'volcano'} key={record?.identifier}>
          {confirmed ? 'Onaylandı' : 'Onaylanmadı'}
        </Tag>
      ),
    },
    {
      title: <div className="table-title-calibri-bold-12">Aktiflik Durumu</div>,
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      fixed: 'right',
      width: 140,
      render: (status, record) => (
        <Switch
          key={record?.identifier}
          defaultChecked={status}
          checkedChildren="Aktif"
          unCheckedChildren="Pasif"
          onChange={async (value) => {
            await handleStatusChange(value, record);
          }}
        />
      ),
    },
    {
      title: <div className="table-title-calibri-bold-12">İşlemler</div>,
      dataIndex: 'identifier',
      key: 'actions',
      align: 'center',
      fixed: 'right',
      width: 350,
      render: (id, record) => (
        <div>
          <CheckAuth authorityCode={authorities.DISPLAY_ALL_RECORDS_MERHANT_INFO}>
            <NavLink
              to={{
                pathname: `/is-yeri-bilgileri`,
                state: { record },
              }}
            >
              <Button className="mr-3" type="primary">
                <HomeOutlined />
                <span>Üye İş Yerleri</span>
              </Button>
            </NavLink>
          </CheckAuth>
          <CheckAuth authorityCode={authorities.UPDATE_SUB_USER}>
            <Button
              type="ghost"
              onClick={() => {
                handleOpenForm();
                setSelectedUser(record);
                setSubmitting(false);
              }}
            >
              <EditOutlined />
            </Button>
          </CheckAuth>
          <CheckAuth authorityCode={authorities.DELETE_SUB_USER}>
            <Popconfirm
              title="Kaydı silmek istediğinize emin misiniz?"
              okText="Evet"
              cancelText="Hayır"
              onConfirm={async () => {
                await deleteUser(id);
              }}
              placement="topRight"
            >
              <Button className="ml-3" type="primary" danger>
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          </CheckAuth>
        </div>
      ),
    },
  ];

  return (
    <PageContainer
      title="Alt Kullanıcı İşlemleri"
      subTitle="Bu ekranda alt kullanıcıları görebilir ve üzerinden işlem yapabilirsiniz."
      extra={[
        <CheckAuth authorityCode={authorities.ADD_SUB_USER} key="user-add">
          <Button
            disabled={loading}
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              handleOpenForm();
            }}
          >
            Ekle
          </Button>
        </CheckAuth>,
      ]}
    >
      <Table
        loading={loading}
        dataSource={users}
        columns={columns}
        pagination={false}
        rowKey="identifier"
      />
      {visible && (
        <AddForm
          selectedUser={selectedUser}
          submitting={submitting}
          error={errors}
          visible={visible}
          handleCloseForm={handleCloseForm}
          handleSubmitForm={handleSubmitForm}
        />
      )}
    </PageContainer>
  );
};

export default User;
